// src/TopicMover.js
import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import { collection, doc, getDocs, setDoc } from "firebase/firestore";

const TopicMover = () => {
  const [topics, setTopics] = useState([]); // [{id, title, questions: []}]
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");

  const [sourceTopicId, setSourceTopicId] = useState("");
  const [destTopicId, setDestTopicId] = useState("");
  const [selectedQuestionKeys, setSelectedQuestionKeys] = useState([]); // keys of selected questions for move

  // Load topics from Firestore
  useEffect(() => {
    const fetchTopics = async () => {
      try {
        setLoading(true);
        const colRef = collection(db, "interview");
        const snap = await getDocs(colRef);

        const items = snap.docs.map((d) => {
          const data = d.data() || {};
          return {
            id: d.id,
            title: data.title || d.id.toUpperCase(),
            description: data.description || "",
            questions: Array.isArray(data.questions) ? data.questions : [],
          };
        });

        items.sort((a, b) =>
          a.title.toLowerCase().localeCompare(b.title.toLowerCase())
        );

        setTopics(items);
        if (items.length > 0) {
          setSourceTopicId(items[0].id);
        }
      } catch (err) {
        console.error("Error loading topics:", err);
        setStatus("Error loading topics from Firestore.");
      } finally {
        setLoading(false);
      }
    };

    fetchTopics();
  }, []);

  const sourceTopic = topics.find((t) => t.id === sourceTopicId) || null;
  const destTopic = topics.find((t) => t.id === destTopicId) || null;
  const sourceQuestions = sourceTopic?.questions || [];

  // helper: build stable key for each question
  const getQuestionKey = (topicId, q) => {
    if (q.id) return `${topicId}::id::${q.id}`;
    if (typeof q.order === "number") return `${topicId}::order::${q.order}`;
    return `${topicId}::idx::${sourceQuestions.indexOf(q)}`;
  };

  const isSelected = (key) => selectedQuestionKeys.includes(key);

  const toggleQuestion = (key) => {
    setSelectedQuestionKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const clearSelection = () => {
    setSelectedQuestionKeys([]);
  };

  const handleSourceChange = (e) => {
    const value = e.target.value;
    setSourceTopicId(value);
    clearSelection();
    setStatus("");

    // If destination equals new source, reset destination
    if (destTopicId === value) {
      setDestTopicId("");
    }
  };

  const handleDestChange = (e) => {
    const value = e.target.value;
    setDestTopicId(value);
    setStatus("");
  };

  const moveSelectedQuestions = async () => {
    if (!sourceTopic || !destTopic) {
      setStatus("Please select both source and destination topics.");
      return;
    }

    if (selectedQuestionKeys.length === 0) {
      setStatus("Please select at least one question to move.");
      return;
    }

    try {
      setStatus("Moving questions...");
      // Build lists
      const selectedQuestions = [];
      const remainingSourceQuestions = [];

      for (const q of sourceQuestions) {
        const key = getQuestionKey(sourceTopic.id, q);
        if (selectedQuestionKeys.includes(key)) {
          selectedQuestions.push(q);
        } else {
          remainingSourceQuestions.push(q);
        }
      }

      if (selectedQuestions.length === 0) {
        setStatus("No questions matched selection. Nothing moved.");
        return;
      }

      // Destination new questions: append at end
      const destQuestions = destTopic.questions || [];
      const combinedDest = [...destQuestions, ...selectedQuestions];

      // Recalculate order values
      const normalizedSource = remainingSourceQuestions.map((q, index) => ({
        ...q,
        order: index,
      }));

      const normalizedDest = combinedDest.map((q, index) => ({
        ...q,
        order: index,
      }));

      // Write to Firestore
      const interviewCol = collection(db, "interview");
      const sourceRef = doc(interviewCol, sourceTopic.id);
      const destRef = doc(interviewCol, destTopic.id);

      await Promise.all([
        setDoc(
          sourceRef,
          {
            questions: normalizedSource,
            totalQuestions: normalizedSource.length,
            updatedAt: new Date().toISOString(),
          },
          { merge: true }
        ),
        setDoc(
          destRef,
          {
            questions: normalizedDest,
            totalQuestions: normalizedDest.length,
            updatedAt: new Date().toISOString(),
          },
          { merge: true }
        ),
      ]);

      // Update local state
      setTopics((prev) =>
        prev.map((t) => {
          if (t.id === sourceTopic.id) {
            return { ...t, questions: normalizedSource };
          }
          if (t.id === destTopic.id) {
            return { ...t, questions: normalizedDest };
          }
          return t;
        })
      );

      clearSelection();
      setStatus(
        `Moved ${selectedQuestions.length} question(s) from ${sourceTopic.title} to ${destTopic.title}.`
      );
    } catch (err) {
      console.error("Error moving questions:", err);
      setStatus("Error moving questions. Check console for details.");
    }
  };

  return (
    <div className="container mt-4">
      <h2 className="mb-3 text-center">Move Questions Between Topics</h2>

      {loading ? (
        <p className="text-center">Loading topics...</p>
      ) : (
        <div className="row">
          {/* Left: Source */}
          <div className="col-md-6 mb-3">
            <div className="card h-100">
              <div className="card-header bg-primary text-white">
                Source Topic
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label">Select Source Topic</label>
                  <select
                    className="form-select"
                    value={sourceTopicId}
                    onChange={handleSourceChange}
                  >
                    <option value="">-- Select Source --</option>
                    {topics.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.title}
                      </option>
                    ))}
                  </select>
                </div>

                {sourceTopic ? (
                  <>
                    <p>
                      <strong>{sourceTopic.title}</strong> has{" "}
                      {sourceQuestions.length} question(s).
                    </p>

                    <div
                      className="border rounded p-2"
                      style={{ maxHeight: "400px", overflowY: "auto" }}
                    >
                      {sourceQuestions.length === 0 ? (
                        <p className="text-muted mb-0">
                          No questions in this topic.
                        </p>
                      ) : (
                        sourceQuestions
                          .slice()
                          .sort((a, b) => {
                            if (
                              typeof a.order === "number" &&
                              typeof b.order === "number"
                            ) {
                              return a.order - b.order;
                            }
                            return (a.question || "")
                              .toLowerCase()
                              .localeCompare((b.question || "").toLowerCase());
                          })
                          .map((q, index) => {
                            const key = getQuestionKey(sourceTopic.id, q);
                            return (
                              <div
                                className="form-check mb-2"
                                key={key}
                              >
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`q_${index}`}
                                  checked={isSelected(key)}
                                  onChange={() => toggleQuestion(key)}
                                />
                                <label
                                  className="form-check-label"
                                  htmlFor={`q_${index}`}
                                >
                                  <strong>
                                    {q.question || q.topic || `Question ${index + 1}`}
                                  </strong>
                                  {q.topic && q.topic !== q.question && (
                                    <> &nbsp; <span className="badge bg-secondary">{q.topic}</span></>
                                  )}
                                </label>
                                {q.answer && (
                                  <div className="small text-muted">
                                    {q.answer.length > 120
                                      ? q.answer.slice(0, 120) + "..."
                                      : q.answer}
                                  </div>
                                )}
                              </div>
                            );
                          })
                      )}
                    </div>
                  </>
                ) : (
                  <p className="text-muted">Please choose a source topic.</p>
                )}
              </div>
            </div>
          </div>

          {/* Right: Destination */}
          <div className="col-md-6 mb-3">
            <div className="card h-100">
              <div className="card-header bg-success text-white">
                Destination Topic
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <label className="form-label">Select Destination Topic</label>
                  <select
                    className="form-select"
                    value={destTopicId}
                    onChange={handleDestChange}
                  >
                    <option value="">-- Select Destination --</option>
                    {topics
                      .filter((t) => t.id !== sourceTopicId)
                      .map((t) => (
                        <option key={t.id} value={t.id}>
                          {t.title}
                        </option>
                      ))}
                  </select>
                </div>

                {destTopic ? (
                  <p>
                    <strong>{destTopic.title}</strong> currently has{" "}
                    {destTopic.questions?.length || 0} question(s).
                  </p>
                ) : (
                  <p className="text-muted">
                    Choose a destination topic different from the source.
                  </p>
                )}

                <hr />

                <button
                  type="button"
                  className="btn btn-primary w-100"
                  disabled={
                    !sourceTopic ||
                    !destTopic ||
                    selectedQuestionKeys.length === 0
                  }
                  onClick={moveSelectedQuestions}
                >
                  Move selected question(s) →
                </button>

                <p className="mt-2 small text-muted">
                  Selected: {selectedQuestionKeys.length} question(s)
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {status && (
        <div className="alert alert-info mt-3" role="alert">
          {status}
        </div>
      )}
    </div>
  );
};

export default TopicMover;
